import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Container, Typography, Button, Link, Stack, IconButton, Drawer, List,
  ListItemButton, ListItemText, Divider, useScrollTrigger,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';

import { LogoMark } from '../Common/Logo';
import { useAuth } from '../../context/AuthContext';

// Shared chrome for the public marketing site: the landing page and the
// legal pages (Terms / Privacy / Data deletion). Section links point at
// anchors on the landing page ("/#features"), which Home.js scrolls to.
export const SITE_NAME = 'Mom N Kidz SocialFlow';
export const CONTACT_EMAIL = 'digitalsensy@gmail.com';

const NAV_LINKS = [
  { to: '/#features', label: 'Features' },
  { to: '/#platforms', label: 'Platforms' },
  { to: '/#how-it-works', label: 'How it works' },
  { to: '/#contact', label: 'Contact' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Product',
    links: [
      { to: '/#features', label: 'Features' },
      { to: '/#platforms', label: 'Platforms' },
      { to: '/#how-it-works', label: 'How it works' },
      { to: '/#security', label: 'Security' },
    ],
  },
  {
    title: 'Account',
    links: [
      { to: '/login', label: 'Log in' },
      { to: '/register', label: 'Create account' },
      { to: '/forgot-password', label: 'Reset password' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { to: '/terms', label: 'Terms of Service' },
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/data-deletion', label: 'Data Deletion' },
    ],
  },
];

const Brand = ({ color = 'inherit' }) => (
  <Link component={RouterLink} to="/" underline="none" color={color} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ borderRadius: '8px', boxShadow: '0 0 0 1px rgba(20,22,31,0.12)', lineHeight: 0 }}>
      <LogoMark size={30} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
      {SITE_NAME}
    </Typography>
  </Link>
);

const TopBar = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  const actions = user ? (
    <Button component={RouterLink} to="/dashboard" variant="contained" disableElevation>Open dashboard</Button>
  ) : (
    <>
      <Button component={RouterLink} to="/login" color="inherit">Log in</Button>
      <Button component={RouterLink} to="/register" variant="contained" disableElevation>Get started</Button>
    </>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="inherit"
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: scrolled ? 'divider' : 'transparent',
        transition: 'border-color 150ms',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 68 }, gap: 2 }}>
          <Brand />
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' }, ml: 4, flex: 1 }}>
            {NAV_LINKS.map((item) => (
              <Button key={item.to} component={RouterLink} to={item.to} color="inherit" sx={{ fontWeight: 500, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                {item.label}
              </Button>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {actions}
          </Stack>
          <IconButton
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, ml: 'auto' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: 280 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Menu</Typography>
          <IconButton aria-label="Close menu" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Divider />
        <List onClick={() => setOpen(false)}>
          {NAV_LINKS.map((item) => (
            <ListItemButton key={item.to} component={RouterLink} to={item.to}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <Stack spacing={1} sx={{ p: 2 }} onClick={() => setOpen(false)}>
          {actions}
        </Stack>
      </Drawer>
    </AppBar>
  );
};

const Footer = () => (
  <Box component="footer" sx={{ backgroundColor: '#12131C', color: '#EDEFF6', pt: { xs: 6, md: 8 }, pb: 4 }}>
    <Container maxWidth="lg">
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 4, md: 6 },
          gridTemplateColumns: { xs: '1fr 1fr', md: '2fr 1fr 1fr 1fr' },
        }}
      >
        <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
          <Brand />
          <Typography variant="body2" sx={{ color: 'rgba(237,239,246,0.65)', mt: 2, maxWidth: 320, lineHeight: 1.7 }}>
            Plan, create and publish to every social platform from one workspace — built for brands and the agencies that run them.
          </Typography>
          <Link href={`mailto:${CONTACT_EMAIL}`} variant="body2" sx={{ display: 'inline-block', mt: 2, color: '#A5ABF6' }}>
            {CONTACT_EMAIL}
          </Link>
        </Box>
        {FOOTER_COLUMNS.map((col) => (
          <Box key={col.title}>
            <Typography variant="overline" sx={{ color: 'rgba(237,239,246,0.5)', fontWeight: 700, letterSpacing: '0.08em' }}>
              {col.title}
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
              {col.links.map((item) => (
                <Link
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  variant="body2"
                  underline="hover"
                  sx={{ color: 'rgba(237,239,246,0.85)', '&:hover': { color: '#FFFFFF' } }}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Box>
        ))}
      </Box>
      <Divider sx={{ borderColor: 'rgba(237,239,246,0.12)', mt: { xs: 5, md: 7 }, mb: 3 }} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'rgba(237,239,246,0.5)' }}>
          © {new Date().getFullYear()} {SITE_NAME}. Operated by Digital Sensy. All rights reserved.
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(237,239,246,0.5)' }}>
          Uses official platform APIs · GDPR-ready
        </Typography>
      </Box>
    </Container>
  </Box>
);

const PublicLayout = ({ children }) => (
  <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
    <TopBar />
    <Box component="main" sx={{ flex: 1 }}>{children}</Box>
    <Footer />
  </Box>
);

export default PublicLayout;
